import {
  StatementPart,
  TemplatePart,
  TemplatePartTypes,
  TextPart
} from "../types";
import { extractUsingRegex } from "./extractUsingRegex";

/**
 * $variable
 * ${statement}
 * $`statement` - to omit from scaffolded generation
 */
const STATEMENT_PART = /\$\{[\s\S]+?\}|\$`[\s\S]+?`|\$\w+/g;
const STATEMENT_DELIMETERS = /(^\$\{+|\}$)|(^\$`+|`$)|\$/g;

const INPUT_PART = /\$\$(\{[\s\S]+?\}|\w+)/g;
const INPUT_DELIMETERS = /^\$\$\{?|\}$/g;

const MARKDOWN_PART = /<md>[\s\S]+?<\/md>/g;
const MARKDOWN_DELIMETERS = /^<md>|<\/md>$/g;

const LATEX_PART = /<tex>[\s\S]+?<\/tex>/g;
const LATEX_DELIMETERS = /^<tex>|<\/tex>$/g;

export function extractTextParts(
  template: string,
  existingParts: TemplatePart[]
): Omit<TextPart, "value">[] {
  if (existingParts.length === 0) {
    return [
      { type: TemplatePartTypes.TEXT, original: template, templatePosition: 0 }
    ];
  }

  const sortedExistingParts = existingParts.sort(
    (a, b) => a.templatePosition - b.templatePosition
  );

  return sortedExistingParts.reduce(
    (acc, currPart, ind) => {
      const prevPart = sortedExistingParts[ind - 1];
      const nextPart = sortedExistingParts[ind + 1];
      const startPos = prevPart
        ? prevPart.templatePosition + prevPart.original.length
        : 0;
      const endPos = currPart.templatePosition;

      if (endPos - startPos > 0) {
        acc.push({
          type: TemplatePartTypes.TEXT,
          original: template.slice(startPos, endPos),
          templatePosition: startPos
        });
      }

      if (!nextPart) {
        const lastPos = endPos + currPart.original.length;
        acc.push({
          type: TemplatePartTypes.TEXT,
          original: template.slice(lastPos),
          templatePosition: lastPos
        });
      }

      return acc;
    },
    [] as Omit<TextPart, "value">[]
  );
}

export function extractStatementParts(template: string) {
  return (
    extractUsingRegex(template, STATEMENT_PART, STATEMENT_DELIMETERS)
      // filter out inputs which also matches the regex `$${..`
      .filter(it => template[it.templatePosition - 1] !== "$")
      .map(({ original, sanitised, templatePosition }) => {
        if (original.startsWith("$[")) {
          return {
            type: "statement",
            templatePosition,
            original,
            statement: sanitised,
            omitFromScaffold: true
          } as StatementPart;
        }
        return {
          type: "statement",
          templatePosition,
          original,
          statement: sanitised
        } as StatementPart;
      })
  );
}

export function extractInputs(template: string) {
  return extractUsingRegex(template, INPUT_PART, INPUT_DELIMETERS);
}

export function extractMarkdown(template: string) {
  return extractUsingRegex(template, MARKDOWN_PART, MARKDOWN_DELIMETERS);
}

export function extractLatex(template: string) {
  return extractUsingRegex(template, LATEX_PART, LATEX_DELIMETERS);
}
